import React from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Clock,
  Users,
  Camera,
  Star,
  ArrowRight,
  Calendar,
  Euro,
  ShipWheel,
  TreePine,
  Home,
  CheckCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useBookingModal } from "../../contexts/BookingModalContext";
import { useTranslation } from "react-i18next";

export const GiethoornPage: React.FC = () => {
  const { t } = useTranslation();
  const { openModal } = useBookingModal();
  const tourHighlights = [
    {
      icon: ShipWheel,
      title: t("1576", "Boat Rides"),
      description: t("1577", "Leisurely boat rides through tranquil canals"),
    },
    {
      icon: Home,
      title: t("1578", "Thatched Houses"),
      description: t("1579", "Admire idyllic thatched-roof houses"),
    },
    {
      icon: TreePine,
      title: t("1580", "Nature Experience"),
      description: t("1581", "Peaceful escape in stunning natural scenery"),
    },
    {
      icon: Camera,
      title: t("1482", "Photography"),
      description: t("1582", "Perfect opportunities for memorable photos"),
    },
  ];

  const tourFeatures = [
    t("1484", "Transportation service available"),
    t("1583", "Flexible tour arrangements"),
    t("1584", "Scenic village exploration"),
    t("1585", "Waterway viewing opportunities"),
    t("1586", "Cultural site visits"),
    t("1489", "Photography opportunities"),
  ];

  const tourDetails = [
    {
      label: t("1490", "Duration"),
      value: t("1491", "Full Day (8-10 hours)"),
      icon: Clock,
    },
    {
      label: t("1492", "Group Size"),
      value: t("1493", "2-8 People"),
      icon: Users,
    },
    {
      label: t("1494", "Starting Point"),
      value: t("1495", "Schiphol Airport"),
      icon: MapPin,
    },
    {
      label: t("1496", "Price From"),
      value: t("1587", "€180 per person"),
      icon: Euro,
    },
  ];

  return (
    <div className="pt-20 bg-gray-900 min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/tours/Giethoorn.webp"
            alt={t("1588", "Giethoorn - Venice of the North")}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-linear-to-r from-gray-900/90 to-gray-800/90" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center space-x-2 bg-primary/20 text-primary px-6 py-3 rounded-full text-sm font-medium mb-6"
            >
              <ShipWheel className="h-4 w-4" />
              <span>{t("1499", "Premium Tour Experience")}</span>
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-bold mb-8">
              {t("1500", "Discover")}{" "}
              <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
                {t("1589", "Giethoorn")}
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed mb-8">
              {t(
                "1590",
                'The "Venice of the North" - Experience one of the Netherlands\' most picturesque villages'
              )}
            </p>

            <div className="flex items-center justify-center space-x-1 mb-8">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 text-warning fill-current" />
              ))}
              <span className="text-white ml-2 text-lg">
                {t("1591", "4.9/5 (127 reviews)")}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tour Overview */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                  {t("1592", "Venice of the North")}
                </h2>
                <p className="text-lg text-gray-200 leading-relaxed mb-6">
                  {t(
                    "1593",
                    'Join us for a tour of Giethoorn, one of the most picturesque villages in the Netherlands! Known as the "Venice of the North," Giethoorn is famous for its tranquil canals, charming wooden bridges, and idyllic thatched-roof houses.'
                  )}
                </p>
                <p className="text-lg text-gray-200 leading-relaxed mb-6">
                  {t(
                    "1594",
                    "Our expert guides will take you on a leisurely boat ride through the village's waterways, where you'll have the opportunity to admire the stunning scenery and learn about the village's history and traditions."
                  )}
                </p>
                <p className="text-lg text-gray-200 leading-relaxed">
                  {t(
                    "1595",
                    "You'll also have the chance to explore the village on foot, visiting local museums and galleries, and sampling delicious Dutch delicacies at quaint cafes and restaurants."
                  )}
                </p>
              </div>

              {/* Tour Highlights */}
              <div className="grid grid-cols-2 gap-4">
                {tourHighlights.map((highlight) => (
                  <motion.div
                    key={highlight.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="bg-gray-800/50 backdrop-blur-xs p-4 rounded-xl border border-gray-700"
                  >
                    <highlight.icon className="h-8 w-8 text-primary mb-3" />
                    <div className="font-semibold text-white text-sm mb-2">
                      {highlight.title}
                    </div>
                    <p className="text-xs text-gray-300 leading-relaxed">
                      {highlight.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Image Gallery */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative overflow-hidden rounded-2xl shadow-xl"
                >
                  <img
                    src="/images/tours/Giethoorn.webp"
                    alt={t("1596", "Giethoorn Canal")}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-gray-900/60 via-transparent to-transparent" />
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative overflow-hidden rounded-2xl shadow-xl mt-8"
                >
                  <img
                    src="/images/tours/Giethoorn.webp"
                    alt={t("1597", "Traditional Houses")}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-gray-900/60 via-transparent to-transparent" />
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative overflow-hidden rounded-2xl shadow-xl col-span-2"
                >
                  <img
                    src="/images/tours/Giethoorn.webp"
                    alt={t("1598", "Boat Tour")}
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-gray-900/60 via-transparent to-transparent" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tour Details */}
      <section className="py-24 bg-gray-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t("1512", "Tour Details & Information")}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t(
                "1599",
                "Everything you need to know about your Giethoorn adventure"
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Tour Details */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gray-800/50 backdrop-blur-xs rounded-3xl p-8 border border-gray-700">
                <h3 className="text-2xl font-bold text-white mb-8">
                  {t("1546", "Quick Details")}
                </h3>

                <div className="space-y-6">
                  {tourDetails.map((detail) => (
                    <motion.div
                      key={detail.label}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                      className="flex items-center space-x-4 p-4 bg-gray-700/30 rounded-xl"
                    >
                      <div className="w-12 h-12 bg-linear-to-r from-primary to-primary-light rounded-xl flex items-center justify-center">
                        <detail.icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-white">
                          {detail.label}
                        </p>
                        <p className="text-gray-300">{detail.value}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Tour Highlights */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gray-800/50 backdrop-blur-xs rounded-3xl p-8 border border-gray-700">
                <h3 className="text-2xl font-bold text-white mb-8">
                  {t("1515", "Tour Highlights")}
                </h3>

                <div className="space-y-4">
                  {tourFeatures.map((item) => (
                    <motion.div
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                      className="flex items-start space-x-3"
                    >
                      <CheckCircle className="h-5 w-5 text-primary mt-1 shrink-0" />
                      <span className="text-gray-200">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-linear-to-r from-primary via-primary-light to-primary rounded-3xl p-12 text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-black/20" />
            <div className="relative">
              <Calendar className="h-16 w-16 mx-auto mb-6 opacity-80" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t("1600", "Ready to Explore Giethoorn?")}
              </h2>
              <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
                {t(
                  "1601",
                  "Book your unforgettable journey to the Venice of the North. Whether you're a nature lover, culture enthusiast, or seeking peaceful escape, Giethoorn has something for everyone."
                )}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={openModal}
                  className="bg-white text-primary px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg flex items-center space-x-2"
                >
                  <Calendar className="h-5 w-5" />
                  <span>{t("1549", "Book This Tour")}</span>
                </motion.button>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="border-2 border-white text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-white hover:text-primary transition-all duration-300 flex items-center space-x-2"
                  >
                    <span>{t("1519", "Ask Questions")}</span>
                    <ArrowRight className="h-5 w-5" />
                  </motion.button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
